/**
 * timing 文書 ⇄ 時間の仕組みの文書（索引 Issue #498 の段階 8 → #643 / #722）。
 *
 * サーバーの timing のスロットの形（`server/synchro_server/ocg/fx_docs.py`）:
 *   v1  旧演出プレビューの上書き集合（古い）
 *   v2  旧演出プレビューの文書（イベントが一次オブジェクト）
 *   v3  汎用の編集ソフト（Issue #642 で削除）の文書を包んだもの `{ version: 3, document: {...} }`
 *   v4  **タイミング調節のプロジェクトファイル**（Issue #643。仕様は `docs/duel-timing.md`）
 *
 * 読むときは v4 / v3 / v2 を時間の仕組みの文書（`duelEditor/timeline/document.js`）に写し、**書くときは必ず v4**。
 * **v1 は読まない**（空の文書にする）—— 写しに要る旧演出プレビューは Issue #639 で消した。本番に v1 の文書は無い
 * （2026-09-30 のバックアップで timing は 1 件だけ・v2）。
 * v2 / v3 から引き継ぐのは**人が動かしたイベントの時刻**（v2 の `events` / v3 の `anchors`）とゾーン（v2 の `zones` → `meta.zones`）・
 * v3 の `meta` だけで、帯ごとの上書き・自由なオブジェクト・トラック（v2 / v3 の `objects` / `tracks`）は捨てる
 * （v4 に書く場所が無く、次に保存した時点でどのみち消える。Issue #722）。
 * `clip_defaults`（尺の上書き。Issue #491 で廃止）と `ops`（応答の時刻。Issue #473 で廃止）も捨てる。
 */
import { emptyDocument, normalizeAnchorOverride, readDocument } from '../duelEditor/timeline/document';
import { cleanBands } from './timingBands';
import { cleanTimingSettings } from './timingSettings';

export const TIMING_VERSION_V3 = 3;
/** **タイミング調節のプロジェクトファイル**（Issue #643。仕様は `docs/duel-timing.md`。サーバーは `ocg/fx_docs.py::validate_timing_v4`） */
export const TIMING_VERSION_V4 = 4;
export const TIMING_FORMAT_V4 = 'synchro-duel-timing';

const isFin = Number.isFinite;

/**
 * 時間の仕組みの文書（`duelEditor/timeline/document.js`）→ **プロジェクトファイル（v4）**。
 * 持つのは 人が動かしたイベントの時刻（`anchors[key].ms` → `events[key].ms`。整数に丸める）・テーマ（`meta.theme`）・
 * 設定（`meta.settings`）・拡張帯（`meta.bands`。Issue #914。Web は描かず落とさず往復させるだけ。あれば書く）・fps だけ
 */
export function toProject(doc) {
  const d = readDocument(doc);
  const events = {};
  Object.entries(d.anchors ?? {}).forEach(([key, a]) => {
    if (isFin(a?.ms)) events[key] = { ms: Math.max(0, Math.round(a.ms)) };
  });
  const theme = typeof d.meta?.theme === 'string' && /^[a-z][a-z0-9_]{0,31}$/.test(d.meta.theme) ? d.meta.theme : null;
  const out = {
    format: TIMING_FORMAT_V4,
    version: TIMING_VERSION_V4,
    fps: Number.isInteger(d.fps) && d.fps >= 1 && d.fps <= 120 ? d.fps : 30,
    theme,
    events,
    settings: cleanTimingSettings(d.meta?.settings),
  };
  const bands = cleanBands(d.meta?.bands);
  if (bands.length) out.bands = bands;
  return out;
}

/** プロジェクトファイル（v4）→ 時間の仕組みの文書 */
export function fromProject(p) {
  const doc = emptyDocument(Number.isInteger(p?.fps) && p.fps > 0 ? p.fps : 30);
  Object.entries(p?.events ?? {}).forEach(([key, e]) => { if (isFin(e?.ms)) doc.anchors[key] = { ms: e.ms }; });
  if (p?.theme) doc.meta.theme = p.theme;
  doc.meta.settings = cleanTimingSettings(p?.settings);
  const bands = cleanBands(p?.bands);
  if (bands.length) doc.meta.bands = bands;
  return doc;
}

/** v2 の文書 → 時間の仕組みの文書（イベントの時刻とゾーンだけ） */
export function fromTimingV2(v2) {
  const doc = emptyDocument(Number.isInteger(v2?.fps) && v2.fps > 0 ? v2.fps : 30);
  for (const [key, v] of Object.entries(v2?.events ?? {})) {
    const o = normalizeAnchorOverride(v);
    if (o) doc.anchors[key] = o;
  }
  if (v2?.zones && Object.keys(v2.zones).length) doc.meta.zones = v2.zones;
  return doc;
}

/**
 * サーバーから読んだ timing → 時間の仕組みの文書。
 */
export function fromTiming(timing) {
  if (timing?.version === TIMING_VERSION_V4) return fromProject(timing);
  if (timing?.version === TIMING_VERSION_V3) return readDocument(timing.document);
  if (timing?.version === 2) return fromTimingV2(timing);
  return emptyDocument(30);
}

/** 時間の仕組みの文書 → 保存する形（**必ず v4**。Issue #643） */
export const toTiming = (doc) => toProject(doc);
